import { message, Popover, Tooltip } from 'antd';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useMessagesAutoTranslate } from './MessageEditor/Toolbar/MessagesAutoTranslate';
import { useRequest } from 'ahooks';
import { modeTranslate, overseaTranslate, TranslateMode } from 'services/api/overseaTranslate';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useTranslate } from '@tolgee/react';
import Icon, { DownOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { ReactComponent as TranslateSvg } from '../../assets/translate.svg';
import { ReactComponent as GoogleSvg } from '../../assets/google.svg';
import { ReactComponent as GptSvg } from '../../assets/gpt.svg';
import { ReactComponent as DeepseekSvg } from '../../assets/deepseek.svg';
import { ReactComponent as Video2textSvg } from './MessageMenu/assets/video2text.svg';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { getVideoAsrText, GetVideoAsrTextBody } from 'services/api/conversation';
import { useCurrentChatBuyerLang } from 'hooks/business/im/chatPreference';
import isEqual from 'react-fast-compare';

const getVideoAsrTextPromiseList: [GetVideoAsrTextBody, Promise<{ asrText: string }>][] = [];
const getVideoAsrTextPromise = (body: GetVideoAsrTextBody) => {
  const target = getVideoAsrTextPromiseList.find((_) => isEqual(body, _[0]));
  if (target) {
    return target[1];
  }
  const removePromise = () => {
    const targetIndex = getVideoAsrTextPromiseList.findIndex((_) => isEqual(body, _[0]));
    if (targetIndex > -1) {
      getVideoAsrTextPromiseList.splice(targetIndex, 1);
    }
  };
  const promise = getVideoAsrText(body)
    .then((res) => {
      removePromise();
      return res;
    })
    .catch((err) => {
      removePromise();
      return Promise.reject(err);
    });
  getVideoAsrTextPromiseList.push([body, promise]);
  return promise;
};

window.test = getVideoAsrTextPromiseList;

export function useVideoText({
  region,
  videoText,
  setVideoText,
  getVideoAsrTextBody,
}: {
  region: General.UpperCaseRegion;
  videoText?: string;
  setVideoText: Dispatch<SetStateAction<string | undefined>>;
  getVideoAsrTextBody: () => Promise<GetVideoAsrTextBody>;
}) {
  const { t } = useTranslate();

  const _autoTranslate = useMessagesAutoTranslate();

  const lang = useSystemLanguageValue();

  const buyerLang = useCurrentChatBuyerLang(region);

  const autoTranslate = _autoTranslate && lang !== buyerLang;

  const refsCurrent = {
    setVideoText,
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (videoText === '') {
      getVideoAsrTextBody()
        .then(getVideoAsrTextPromise)
        .then(({ asrText }) => {
          if (!asrText) {
            message.error(refs.current.t('未识别到视频内容'));
            refs.current.setVideoText(undefined);
          } else {
            refs.current.setVideoText(asrText);
          }
        })
        .catch(() => {
          message.error(refs.current.t('未识别到视频内容'));
          refs.current.setVideoText(undefined);
        });
    }
  }, [getVideoAsrTextBody, videoText]);

  const [mode, setMode] = useState<TranslateMode>();

  const { data, loading, error, runAsync, refreshAsync } = useRequest(
    (mode?: TranslateMode) => {
      setMode(mode);
      if (!mode) {
        return overseaTranslate(videoText || '', lang, 'videoText');
      }
      return modeTranslate(videoText || '', lang, mode, { from: 'videoText' });
    },
    {
      manual: !autoTranslate,
      ready: Boolean(videoText),
    },
  );

  const translateContent = error ? (
    <TranslateError onClick={() => refreshAsync()}>
      <ReloadOutlined />
      {t('Entities.翻译失败')}
    </TranslateError>
  ) : loading ? (
    <TranslateLoading>
      <LoadingOutlined />
      {data ? `${t('ViewsChat.重新翻译中')}...` : t('Entities.翻译中')}
    </TranslateLoading>
  ) : data ? (
    <TranslateSuccess>
      {mode &&
        {
          GOOGLE: <Icon component={GoogleSvg} />,
          CHAT_GPT: <Icon component={GptSvg} />,
          DEEPSEEK: <Icon component={DeepseekSvg} />,
        }[mode]}
      {data}
    </TranslateSuccess>
  ) : null;

  const translateTrigger = (
    <Popover
      getPopupContainer={(_) => _}
      placement="bottom"
      align={{ offset: [0, -6] }}
      overlayClassName={styles.popover}
      content={[
        <Tooltip
          getTooltipContainer={(_) => _}
          title={
            data
              ? t('ViewsChat.使用x重新翻译', { x: 'Google' })
              : t('ViewsChat.使用x翻译', { x: 'Google' })
          }
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              runAsync('GOOGLE');
            }}
          >
            <Icon component={GoogleSvg} />
          </div>
        </Tooltip>,
        <Tooltip
          getTooltipContainer={(_) => _}
          title={
            data
              ? t('ViewsChat.使用x重新翻译', { x: 'ChatGPT' })
              : t('ViewsChat.使用x翻译', { x: 'ChatGPT' })
          }
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              runAsync('CHAT_GPT');
            }}
          >
            <Icon component={GptSvg} />
          </div>
        </Tooltip>,
        <Tooltip
          getTooltipContainer={(_) => _}
          title={
            data
              ? t('ViewsChat.使用x重新翻译', { x: 'DeepSeek' })
              : t('ViewsChat.使用x翻译', { x: 'DeepSeek' })
          }
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              runAsync('DEEPSEEK');
            }}
          >
            <Icon component={DeepseekSvg} />
          </div>
        </Tooltip>,
      ]}
    >
      <Trigger>
        <Icon component={TranslateSvg} />
        <DownOutlined />
      </Trigger>
    </Popover>
  );

  return {
    translateContent,
    translateTrigger,
  };
}

function VideoText({
  region,
  videoText,
  setVideoText,
  getVideoAsrTextBody,
}: {
  region: General.UpperCaseRegion;
  videoText: string;
  setVideoText: Dispatch<SetStateAction<string | undefined>>;
  getVideoAsrTextBody: () => Promise<GetVideoAsrTextBody>;
}) {
  const { translateContent, translateTrigger } = useVideoText({
    region,
    videoText,
    setVideoText,
    getVideoAsrTextBody,
  });

  if (!videoText) {
    return (
      <Container>
        <section>
          <section>
            <LoadingOutlined />
          </section>
        </section>
      </Container>
    );
  }

  return (
    <Container>
      <section>
        <p>{videoText}</p>
        {translateContent}
        <Icon component={Video2textSvg} />
      </section>
      <div>{translateTrigger}</div>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: flex-end;
  > section {
    position: relative;
    max-width: 436px;
    padding: 12px;
    border-radius: 4px;
    background: #fff;
    word-break: break-all;
    overflow-wrap: break-word;
    white-space: pre-line;
    > p {
      margin-bottom: 8px;
    }
    > div {
      padding-top: 8px;
      border-top: 1px solid #ececec;
    }
    > span {
      position: absolute;
      top: 0;
      right: 0;
      background: #f0f7ff;
    }
  }
  > div {
    margin: 0 6px;
  }
`;

const Translate = styled.div`
  font-size: 12px;
  > .anticon {
    font-size: 16px;
    margin-right: 2px;
  }
`;

const TranslateLoading = styled(Translate)`
  color: #85b1ff;
`;

const TranslateError = styled(Translate)`
  color: #336fff;
  cursor: pointer;
`;

const TranslateSuccess = styled(Translate)`
  font-size: 14px;
  color: #84868c;
`;

const Trigger = styled.span`
  display: flex;
  align-items: center;
  gap: 0 2px;
  height: 24px;
  padding: 0 4px;
  color: #84868c;
  background: #fff;
  border: solid 1px #d3d7e0;
  cursor: pointer;
  .anticon-down {
    font-size: 10px;
  }
`;

const styles = {
  popover: css`
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-inner-content {
      display: flex;
      align-items: center;
      padding: 4px;
      > div {
        width: 24px;
        height: 24px;
        line-height: 24px;
        text-align: center;
        font-size: 16px;
        cursor: pointer;
        &:hover {
          background: #f0f2f5;
        }
      }
    }
  `,
};

export default VideoText;
